import React, { useState, useEffect } from "react"

export default function DashboardManager() {
  const [show, setShow] = useState(null)
  const [loading, setLoading] = useState(true)
  const [currentGameId, setCurrentGameId] = useState(null)
  const [expandedGames, setExpandedGames] = useState({})
  const [selectedSuggestions, setSelectedSuggestions] = useState({})
  useEffect(() => {
    fetchCurrentShow()
  }, [])

  const fetchCurrentShow = async () => {
    try {
      const response = await fetch("/api/current_show")
      const data = await response.json()
      setShow(data)
      setCurrentGameId(data?.current_game_id || null)
      setLoading(false)
    } catch (error) {
      console.error("Error fetching current show:", error)
      setLoading(false)
    }
  }

  const updateShowStatus = async (newStatus) => {
    try {
      const response = await fetch(`/api/admin/shows/${show.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content,
        },
        body: JSON.stringify({
          show: { status: newStatus }
        }),
      })

      if (response.ok) {
        setShow({ ...show, status: newStatus })
      } else {
        console.error("Failed to update show status")
      }
    } catch (error) {
      console.error("Error updating show status:", error)
    }
  }

  const updateGameWhatHappened = async (gameId, whatHappened) => {
    try {
      await fetch(`/api/admin/games/${gameId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content,
        },
        body: JSON.stringify({
          game: { what_happened: whatHappened }
        }),
      })
    } catch (error) {
      console.error("Error updating game:", error)
    }
  }

  const handleGameChange = (gameId, whatHappened) => {
    // Update local state immediately
    const updatedGames = show.games.map(game =>
      game.id === gameId ? { ...game, what_happened: whatHappened } : game
    )
    setShow({ ...show, games: updatedGames })

    // Debounce the API call
    updateGameWhatHappened(gameId, whatHappened)
  }

  const handleShowOnProjectorChange = async (gameId, showOnProjector) => {
    try {
      // Update local state immediately
      const updatedGames = show.games.map(game =>
        game.id === gameId ? { ...game, show_on_projector: showOnProjector } : game
      )
      setShow({ ...show, games: updatedGames })

      // Update via API
      await fetch(`/api/admin/games/${gameId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content,
        },
        body: JSON.stringify({
          game: { show_on_projector: showOnProjector }
        }),
      })
    } catch (error) {
      console.error("Error updating game:", error)
    }
  }

  const setCurrentGame = async (gameId) => {
    try {
      const response = await fetch(`/api/admin/shows/${show.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content,
        },
        body: JSON.stringify({
          show: { current_game_id: gameId }
        }),
      })

      if (response.ok) {
        setCurrentGameId(gameId)
        setShow({ ...show, current_game_id: gameId })
      } else {
        console.error("Failed to set current game")
      }
    } catch (error) {
      console.error("Error setting current game:", error)
    }
  }

  const resetDisplayedAskfors = async (gameId) => {
    try {
      const response = await fetch(`/api/admin/games/${gameId}/reset_displayed`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content,
        },
      })

      if (response.ok) {
        alert("Displayed ask-fors have been reset for this game")
        fetchCurrentShow()
      } else {
        console.error("Failed to reset displayed ask-fors")
      }
    } catch (error) {
      console.error("Error resetting displayed ask-fors:", error)
    }
  }

  const toggleGameExpansion = (gameId) => {
    setExpandedGames(prev => ({
      ...prev,
      [gameId]: !prev[gameId]
    }))
  }

  const toggleSuggestionSelection = (gameId, suggestionId) => {
    setSelectedSuggestions(prev => {
      const gameSelections = prev[gameId] || []
      if (gameSelections.includes(suggestionId)) {
        return {
          ...prev,
          [gameId]: gameSelections.filter(id => id !== suggestionId)
        }
      } else {
        return {
          ...prev,
          [gameId]: [...gameSelections, suggestionId]
        }
      }
    })
  }

  const hideSuggestions = async (gameId) => {
    const ids = selectedSuggestions[gameId] || []
    if (ids.length === 0) return

    try {
      const response = await fetch("/api/admin/parsed_responses/hide_multiple", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content,
        },
        body: JSON.stringify({ ids }),
      })

      if (response.ok) {
        // Clear selections and refresh
        setSelectedSuggestions(prev => ({ ...prev, [gameId]: [] }))
        fetchCurrentShow()
      } else {
        console.error("Failed to hide suggestions")
      }
    } catch (error) {
      console.error("Error hiding suggestions:", error)
    }
  }

  const unhideSuggestion = async (suggestionId) => {
    try {
      const response = await fetch(`/api/admin/parsed_responses/${suggestionId}/unhide`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector("[name='csrf-token']").content,
        },
      })

      if (response.ok) {
        fetchCurrentShow()
      } else {
        console.error("Failed to unhide suggestion")
      }
    } catch (error) {
      console.error("Error unhiding suggestion:", error)
    }
  }

  if (loading) {
    return <div className="text-center py-8">Loading...</div>
  }

  if (!show) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No active show. Create and activate a show first!</p>
      </div>
    )
  }

  const statuses = [
    "Waiting to begin",
    "First half",
    "Second half",
    "Intermission",
    "Complete"
  ]

  const sortedGames = (show.games || []).sort((a, b) => a.order_in_show - b.order_in_show)

  return (
    <div>
      <h2 className="text-2xl font-semibold text-gray-800 mb-6">Dashboard</h2>

      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <h3 className="text-3xl font-bold text-gray-800 mb-4">{show.title}</h3>

        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Show Status
          </label>
          <select
            value={show.status || "Waiting to begin"}
            onChange={(e) => updateShowStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
          >
            {statuses.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="text-xl font-semibold text-gray-800 mb-4">Games</h4>

        {sortedGames.length === 0 ? (
          <p className="text-gray-500 italic">No games in this show yet.</p>
        ) : (
          <div className="space-y-4">
            {sortedGames.map((game) => (
              <div key={game.id} className="border-b border-gray-200 pb-4 last:border-b-0">
                <div className="flex items-center gap-2 mb-2">
                  <h5 className="font-medium text-gray-800">
                    {game.order_in_show}. {game.game_type}
                  </h5>
                  <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded">
                    {game.votes_count || 0} {game.votes_count === 1 ? 'vote' : 'votes'}
                  </span>
                  {currentGameId === game.id && (
                    <span className="px-2 py-1 bg-cyan-500 text-white text-xs font-semibold rounded">
                      CURRENT
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-2 mb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={game.show_on_projector}
                      onChange={(e) => handleShowOnProjectorChange(game.id, e.target.checked)}
                      className="w-4 h-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
                    />
                    <span className="text-sm text-gray-700">Show on projector</span>
                  </label>
                </div>

                <div className="flex gap-2 mb-2">
                  {currentGameId !== game.id ? (
                    <button
                      onClick={() => setCurrentGame(game.id)}
                      className="px-3 py-1 bg-cyan-500 hover:bg-cyan-600 text-white text-sm font-semibold rounded transition-colors"
                    >
                      Set Current
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => setCurrentGame(null)}
                        className="px-3 py-1 bg-gray-300 hover:bg-gray-400 text-gray-700 text-sm font-semibold rounded transition-colors"
                      >
                        Clear Current
                      </button>
                      {game.needs_askfor && (
                        <button
                          onClick={() => resetDisplayedAskfors(game.id)}
                          className="px-3 py-1 bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded transition-colors"
                        >
                          Reset Displayed
                        </button>
                      )}
                    </>
                  )}
                </div>

                <label className="block text-sm text-gray-600 mb-1">
                  What happened:
                </label>
                <textarea
                  value={game.what_happened || ""}
                  onChange={(e) => handleGameChange(game.id, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent resize-y"
                  rows="3"
                  placeholder="Describe what happened in this game..."
                />

                {/* Expandable suggestions section */}
                {game.needs_askfor && game.parsed_responses && (
                  <div className="mt-4">
                    <button
                      onClick={() => toggleGameExpansion(game.id)}
                      className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                    >
                      <span className={`transform transition-transform ${expandedGames[game.id] ? 'rotate-90' : ''}`}>
                        ▶
                      </span>
                      Suggestions ({game.parsed_responses.filter(s => !s.hidden).length} visible, {game.parsed_responses.filter(s => s.hidden).length} hidden)
                    </button>

                    {expandedGames[game.id] && (
                      <div className="mt-3 border border-gray-200 rounded-lg p-3 bg-gray-50">
                        {/* Action button */}
                        {(selectedSuggestions[game.id]?.length > 0) && (
                          <div className="mb-3">
                            <button
                              onClick={() => hideSuggestions(game.id)}
                              className="px-2 py-1 text-xs bg-red-500 hover:bg-red-600 text-white rounded font-semibold"
                            >
                              Don't Use ({selectedSuggestions[game.id].length})
                            </button>
                          </div>
                        )}

                        {/* Visible suggestions */}
                        <div className="space-y-1">
                          <p className="text-xs font-semibold text-gray-500 mb-2">Visible Suggestions:</p>
                          {game.parsed_responses.filter(s => !s.hidden).length === 0 ? (
                            <p className="text-sm text-gray-400 italic">No visible suggestions</p>
                          ) : (
                            game.parsed_responses.filter(s => !s.hidden).map(suggestion => (
                              <label
                                key={suggestion.id}
                                className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-100 ${
                                  selectedSuggestions[game.id]?.includes(suggestion.id) ? 'bg-cyan-50 border border-cyan-200' : ''
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedSuggestions[game.id]?.includes(suggestion.id) || false}
                                  onChange={() => toggleSuggestionSelection(game.id, suggestion.id)}
                                  className="w-4 h-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
                                />
                                <span className="text-sm text-gray-800">
                                  {suggestion.content}
                                </span>
                              </label>
                            ))
                          )}
                        </div>

                        {/* Hidden suggestions */}
                        {game.parsed_responses.filter(s => s.hidden).length > 0 && (
                          <div className="mt-4 pt-3 border-t border-gray-200">
                            <p className="text-xs font-semibold text-gray-500 mb-2">Hidden Suggestions:</p>
                            <div className="space-y-1">
                              {game.parsed_responses.filter(s => s.hidden).map(suggestion => (
                                <div
                                  key={suggestion.id}
                                  className="flex items-center justify-between p-2 rounded bg-red-50"
                                >
                                  <span className="text-sm text-gray-500 line-through">
                                    {suggestion.content}
                                  </span>
                                  <button
                                    onClick={() => unhideSuggestion(suggestion.id)}
                                    className="px-2 py-1 text-xs bg-green-500 hover:bg-green-600 text-white rounded"
                                  >
                                    Restore
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
